// externals
import type { AnyAction, Reducer } from 'redux';
import type { LenderAddressState } from '@makemydeal/dr-dash-types';

// consts/enums
import {
    FETCH_LENDER_ADDRESS_REQUEST,
    FETCH_LENDER_ADDRESS_SUCCESS,
    FETCH_LENDER_ADDRESS_FAILURE,
    CLEAR_LENDER_ADDRESS_ERROR,
    CACHE_LENDER_ADDRESS_EDIT,
    CACHE_LENDER_ITEM
} from '../actionTypes/lenderAddressActionTypes';
import { OFFER_SAVE_SUCCESS } from '../actionTypes/offerActionTypes';

export type LenderAddressReducer = Reducer<LenderAddressState>;

const defaultState: LenderAddressState = {
    lenderAddressFetchStatus: {
        isLoading: false,
        hasError: false
    },
    cachedLenders: {}
};
/**
 * Reducer for handling the lenderAddress API call async states[loading, error] along with cache
 * Cache is stored with this format
 * {
 *     scenario1:{
 *         lenderAddress:{
 *             cachedLenders: {
 *                 finance: {
 *                     lender1: `Cached lender data for the lender1 for `Finance` deal type for `Scenario1`
 *                 },
 *                 lease: {
 *                     // Here we will have separate copy for the lease deal type as the response for both deal type would vary
 *                     lender1: `Cached lender data for the lender1 for `Lease` deal type for `Scenario1`
 *                 }
 *             }
 *         }
 *     },
 *     scenario2:{
 *         lenderAddress:{
 *             cachedLenders: {
 *                 finance: {
 *                     // Here we will have separate copies for cache for different scenarios
 *                     lender1: `Cached lender data for the lender1 for `Finance` deal type for `Scenario2`
 *                 }
 *             }
 *         }
 *     }
 *
 * }
 */
export const reducer: LenderAddressReducer = (state = defaultState, action: AnyAction) => {
    switch (action.type) {
        /**
         * Here the `_REQUEST`, `_SUCCESS` and `_FAILURE` data is not kept with `lenderId` scope,
         * because when updating the lender we are disabling the lender selection
         */
        case FETCH_LENDER_ADDRESS_REQUEST:
            return { ...state, lenderAddressFetchStatus: { isLoading: true, hasError: false } };
        case FETCH_LENDER_ADDRESS_SUCCESS:
            return { ...state, lenderAddressFetchStatus: { isLoading: false, hasError: false } };
        case FETCH_LENDER_ADDRESS_FAILURE:
            return { ...state, lenderAddressFetchStatus: { isLoading: false, hasError: true } };
        case CLEAR_LENDER_ADDRESS_ERROR:
        case OFFER_SAVE_SUCCESS:
            return { ...state, lenderAddressFetchStatus: { ...state.lenderAddressFetchStatus, hasError: false } };
        /**
         * Here we are partially caching only titling lender data for when user is updating the titling lender address
         */
        case CACHE_LENDER_ADDRESS_EDIT: {
            const { offerType, lenderId, address } = action.payload;
            return {
                ...state,
                cachedLenders: {
                    ...state.cachedLenders,
                    [offerType]: {
                        ...state.cachedLenders[offerType],
                        [lenderId]: { ...state.cachedLenders[offerType]?.[lenderId], titlingLender: address }
                    }
                }
            };
        }
        case CACHE_LENDER_ITEM: {
            const { offerType, lenderId, titlingLender, creditDecisionLender } = action.payload;
            return {
                ...state,
                cachedLenders: {
                    ...state.cachedLenders,
                    [offerType]: {
                        ...state.cachedLenders[offerType],
                        [lenderId]: { ...state.cachedLenders[offerType]?.[lenderId], titlingLender, creditDecisionLender }
                    }
                }
            };
        }
        default:
            return state;
    }
};
